import React, { useEffect, useMemo, useState } from 'react';
import {
  UserOutlined,
  LogoutOutlined,
  CodeOutlined,
  DashboardOutlined,
  AppstoreOutlined,
  FolderOpenOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Menu, Tooltip } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from './../logo.svg';
import { useKeycloak } from '@react-keycloak/web';
import { WORKSPACE_NAV_PRIMARY, WORKSPACE_NAV_VISUALIZATION, getMenuKeyForPath } from '../config/workspaceNav';
import { useAppTheme } from '../hooks/useAppTheme';
import './Sidebar.css';

function getItem(label, key, icon, path, children = undefined, type) {
  return {
    key,
    icon,
    path,
    children: Array.isArray(children) ? children : undefined,
    label: path ? <Link to={path}>{label}</Link> : label,
    type,
  };
}

const findItemPath = (items, key) => {
  for (const item of items) {
    if (item.key === key && item.path) return item.path;
    if (item.children) {
      const childPath = findItemPath(item.children, key);
      if (childPath) return childPath;
    }
  }
  return null;
};

const SideBar = ({ collapsed, onToggle }) => {
  const { isLight } = useAppTheme();
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const location = useLocation();

  const items = useMemo(
    () => [
      { type: 'divider' },
      getItem('Home', '1', <DashboardOutlined />, '/home'),
      { type: 'divider' },
      {
        key: 'workspace',
        icon: <AppstoreOutlined />,
        label: 'Workspace',
        children: [
          ...WORKSPACE_NAV_PRIMARY.map((ws) => getItem(ws.label, ws.key, null, ws.path)),
          {
            type: 'group',
            label: 'Visualizations',
            key: 'workspace-visualizations',
            children: WORKSPACE_NAV_VISUALIZATION.map((ws) => getItem(ws.label, ws.key, null, ws.path)),
          },
        ],
      },
      { type: 'divider' },
      getItem('File Explorer', 'ws-file-explorer', <FolderOpenOutlined />, '/workspace/file-explorer'),
      { type: 'divider' },
      getItem('Console', '20', <CodeOutlined />, '/console'),
      { type: 'divider' },
      getItem('Account', 'account', <UserOutlined />),
    ],
    [],
  );

  const selectedKey = getMenuKeyForPath(location.pathname);
  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    if (location.pathname.startsWith('/workspace')) {
      setOpenKeys((prev) => (prev.includes('workspace') ? prev : ['workspace', ...prev]));
    }
  }, [location.pathname]);

  const onMenuItemClick = (e) => {
    if (e.key === 'account') {
      navigate('/account');
    } else if (e.key !== 'workspace') {
      const path = findItemPath(items, e.key);
      if (path) navigate(path);
    }
  };

  return (
    <div className='sidebar'>
      <div className={`sidebar__brand ${collapsed ? 'sidebar__brand--collapsed' : ''}`}>
        <button
          type='button'
          className='sidebar__menu-btn'
          onClick={onToggle}
          aria-label={collapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
        >
          <MenuOutlined className='sidebar__menu-btn__icon' />
        </button>
        {!collapsed && (
          <div className='sidebar__logo'>
            <img src={logo} alt='EO4EU' />
          </div>
        )}
      </div>
      <Menu
        className='sidebar__menu'
        selectedKeys={[selectedKey]}
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        mode='inline'
        theme={isLight ? 'light' : 'dark'}
        items={items}
        onClick={onMenuItemClick}
      />

      <div className={`sidebar__bottom ${collapsed ? 'sidebar__bottom--collapsed' : ''}`}>
        <Tooltip placement='right' title={collapsed ? 'User Guide' : null}>
          <Link className='sidebar__bottom-link' to='/user-guides'>
            <span className='sidebar__bottom-icon' aria-hidden>
              {/* Use same icon set as menu */}
              <svg viewBox='0 0 24 24' width='18' height='18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  d='M6 4.75h9.25a2 2 0 0 1 2 2V20.5a.75.75 0 0 1-1.14.64L12 18.64l-4.11 2.5A.75.75 0 0 1 6.75 20.5V6.75a2 2 0 0 1 2-2Z'
                  stroke='currentColor'
                  strokeWidth='1.5'
                />
              </svg>
            </span>
            {!collapsed && <span className='sidebar__bottom-text'>User Guide</span>}
          </Link>
        </Tooltip>

        <Tooltip placement='right' title={collapsed ? 'Sign Out' : null}>
          <button
            type='button'
            className='sidebar__bottom-link sidebar__bottom-link--button'
            onClick={() => window.open(keycloak.createLogoutUrl(), '_self')}
          >
            <LogoutOutlined className='sidebar__bottom-anticon' />
            {!collapsed && <span className='sidebar__bottom-text'>Sign Out</span>}
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default SideBar;
